import { Controller } from "@hotwired/stimulus"
import { assessmentQuestionAnswered } from "../utils/assessment_answered"

// Blocks an assessment (Course Test / Evaluation) submission until every
// question is answered, marking each unanswered question with an inline error
// that clears as soon as the learner answers it.
//
// In the course PREVIEW it also stands in for the submit itself (TMS-891): the same
// completeness check runs, then the form locks in place and says so, without ever
// posting. A reviewer sees the assessment behave end to end while no
// AssessmentSubmission is written for a course that may not even be published.
export default class extends Controller {
  static targets = ["question", "errorTemplate", "previewBanner", "answerAgainButton", "submitButton"]
  static values = { preview: Boolean }

  submit(event) {
    const unanswered = this.questionTargets.filter((question) => !this.answered(question))
    this.questionTargets.forEach((question) => this.clearError(question))

    if (unanswered.length > 0) {
      event.preventDefault()
      unanswered.forEach((question) => this.showError(question))
      this.bringIntoView(unanswered[0])
      return
    }

    if (!this.previewValue) return

    // Complete, but this is a preview — stop the post and render the submitted state
    // ourselves. The form's action is already inert; preventing it here is what keeps
    // that from being the only thing standing between a reviewer and a real write.
    event.preventDefault()
    this.lockForPreview(true)
  }

  answerAgain(event) {
    event.preventDefault()
    this.lockForPreview(false)
    this.questionTargets[0]?.querySelector("input, textarea, select")?.focus()
  }

  lockForPreview(locked) {
    this.element.classList.toggle("is-locked", locked)
    this.element
      .querySelectorAll("input, textarea, select")
      .forEach((field) => { field.disabled = locked })

    if (this.hasPreviewBannerTarget) this.previewBannerTarget.hidden = !locked
    if (this.hasAnswerAgainButtonTarget) this.answerAgainButtonTarget.hidden = !locked
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.hidden = locked
  }

  // Marks whatever is unanswered inside the element the event came from. The
  // Evaluation walkthrough fires this at the screen a learner is stepping off, so a
  // question they skipped says so there and then rather than at the very end.
  markWithin(event) {
    this.questionTargets
      .filter((question) => event.target.contains(question))
      .forEach((question) => (this.answered(question) ? this.clearError(question) : this.showError(question)))
  }

  clear(event) {
    const question = event.target.closest("[data-academy-assessment-target='question']")
    if (question && this.answered(question)) this.clearError(question)
  }

  answered(question) {
    return assessmentQuestionAnswered(question)
  }

  showError(question) {
    if (question.classList.contains("is-unanswered")) return

    question.classList.add("is-unanswered")
    question.appendChild(this.errorTemplateTarget.content.cloneNode(true))
  }

  clearError(question) {
    question.classList.remove("is-unanswered")
    question.querySelector(".academy-quiz-question-error")?.remove()
  }

  // On the Evaluation the flagged question can be on a screen the learner cannot see,
  // so the walkthrough is asked to open it before the scroll. On the scrolling list
  // nothing is listening and the scroll is all that happens.
  bringIntoView(question) {
    question.dispatchEvent(new CustomEvent("assessment:reveal", { bubbles: true }))
    question.scrollIntoView({ behavior: "smooth", block: "center" })
  }
}
